import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import IconSearch from "../../../assets/svg/search-icon-grey.svg";
import toast from "react-hot-toast";
import config from "../../../config";
import axiosInstant from "../../../api";
import { useUser } from "../../../hooks/useUser";
import ListView from "./components/ListView";
import { styled } from "@mui/material";
import { isInternal } from "../../../utils";
import ListingFilters from "../../../components/ListingFilters";
import { Link } from "react-router-dom";
import { PageTitle } from "../../../components/PageTitle";
import { MainContainer } from "../../../components/ContentWrapper";
import useDebounce from "../../../hooks/useDebounce";
import { DotLottleLoader, LoadMoreBtn, PageDivider } from "../../../components/Loader";
import SortControls from "../../../components/SortControls/SortControls";
import { useCommonParams } from "../../../hooks/useCommonParams";
import { usePagination } from "../../../hooks/usePagination";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog/ConfirmationDialog";

const PAGE_SIZE = 20;

export default function BrowseUsers() {
  const { role, user } = useUser();
  const {
    searchParams,
    setSearchParams,
    searchText,
    setSearchText,
    page,
    setPage,
    sortType,
    setSortType,
    getSortParam,
    setSortDirection,
  } = useCommonParams({ defaultSortKey: "dateCreated" });

  const [localSearchInput, setLocalSearchInput] = useState(
    searchParams.get("search") || ""
  );
  const [userToDelete, setUserToDelete] = useState(null);
  const debouncedSearchTerm = useDebounce(localSearchInput, 500);

  useEffect(() => {
    setSearchText(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  const onSearchChange = (e) => {
    setLocalSearchInput(e.target.value);
  };

  const sortParam = getSortParam();
  const brandFilter = searchParams.get("brand") || "";

  const fetchFn = useCallback(
    async (pageNum) => {
      try {
        const params = {
          users: true,
          page: pageNum,
          limit: PAGE_SIZE,
          sort: sortParam,
        };
        if (searchText) params.search = searchText;
        if (brandFilter) params.brand = brandFilter;

        const response = await axiosInstant.get("/admin/users/", { params });
        const { rows, count } = response.data;
        return { rows: rows ?? [], count: count ?? 0, error: null };
      } catch (e) {
        if (e?.response?.status === 404) {
          return { rows: [], count: 0, error: null };
        }
        const msg = e?.response?.data?.error ?? "Failed to load users";
        toast.error(msg);
        return { rows: [], count: 0, error: msg };
      }
    },
    [searchText, sortParam, brandFilter],
  );

  const {
    items: users,
    totalCount: userCount,
    loading,
    hasMore,
    canLoadPrevious,
    loadingPrevious,
    error,
    initLoad,
    loadMore,
    loadPrevious,
    topSentinelRef,
  } = usePagination({
    fetchFn,
    pageSize: PAGE_SIZE,
    deepLink: {
      page,
      setPage,
      searchParams,
      setSearchParams,
      ignoreParams: ["page"],
    },
  });

  const deleteUser = () => {
    if (!userToDelete) return;
    const id = userToDelete;
    setUserToDelete(null);
    axiosInstant
      .delete(config.API_URL + "/proteam/users/" + id)
      .then(() => {
        toast.success("User has been successfully removed.");
        initLoad(page);
      })
      .catch(() => {
        toast.error("Something went wrong. Try again.");
      });
  };

  return (
    <MainContainer>
      <div className="flex flex-row items-center justify-between gap-3">
        <PageTitle title="Users" />
        <Link to="/users/admin-create" className="shrink-0">
          <div className="bg-accent text-sm sm:text-base text-white py-2.5 sm:py-3 px-4 sm:px-5 lg:px-10 cursor-pointer text-center rounded-xl focus:outline-none focus:shadow-outline whitespace-nowrap">
            Create User
          </div>
        </Link>
      </div>
      <div className="pt-6 sm:pt-8 pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="relative w-full sm:max-w-[333px]">
            <img
              className="absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 left-5"
              src={IconSearch}
            />
            <input
              type="text"
              className="w-full h-[40px] px-[2.25rem] py-0 gap-2 bg-surface-2 text-text-2 placeholder-muted rounded-xl focus:outline-none focus:text-text-1"
              id="search-experts"
              placeholder="Search Users"
              value={localSearchInput}
              onChange={onSearchChange}
            />
          </div>
          <div className="flex gap-2 items-center justify-end">
            <SortControls
              isAscending={sortType.isAscending}
              onDirectionChange={setSortDirection}
            />
            <ListingFilters
              shouldShowFilter={isInternal(role)}
              showBrandFilter
              onSearchUpdate={() => {}}
              type="users"
              searchParams={searchParams}
              setSearchParams={setSearchParams}
            />
          </div>
        </div>
      </div>

      <PageDivider
        count={userCount}
        label="Users"
        className="mt-5 mb-5"
        canLoadPrevious={canLoadPrevious}
        hasMore={hasMore}
        onLoadPrevious={loadPrevious}
      />

      {loading && users.length === 0 ? (
        <DotLottleLoader />
      ) : (
        <>
          {canLoadPrevious && <div ref={topSentinelRef} style={{ height: 1 }} />}
          {loadingPrevious && (
            <div className="flex justify-center py-2">
              <DotLottleLoader fill={false} sx={{ width: 100, height: 100 }} />
            </div>
          )}
          <ListView pros={users} deleteUser={(id) => setUserToDelete(id)} />
        </>
      )}
      {users.length > 0 && hasMore && (
        <LoadMoreBtn onClick={loadMore} loading={loading} />
      )}
      <ConfirmationDialog
        open={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        onConfirm={deleteUser}
        title="Delete User"
        description="Are you sure you want to delete this user?"
        confirmLabel="Delete"
      />
    </MainContainer>
  );
}
